import { useEffect, useState } from 'react';
import { Button } from 'antd';
import useSheetAnim from '../hooks/useSheetAnim';

// Shared filter UI for list pages (Sales, Listings): a top-right funnel button with
// an active-count badge + a bottom-sheet dialog of single-select chip sections with
// Reset / Apply. Selections persist across sessions via localStorage (loadFilters /
// saveFilters), mirroring the filter set the admin portal offers on its tables.

export const loadFilters = (key) => {
  try {
    const raw = JSON.parse(localStorage.getItem(key)) || {};
    // Sections are multi-select (arrays). Older saves stored single strings —
    // normalize so pre-existing preferences survive the upgrade.
    const out = {};
    for (const [k, v] of Object.entries(raw)) {
      if (Array.isArray(v)) { if (v.length) out[k] = v; }
      else if (v) out[k] = [v];
    }
    return out;
  } catch (_) { return {}; }
};
export const saveFilters = (key, v) => {
  try { localStorage.setItem(key, JSON.stringify(v || {})); } catch (_) { /* private mode */ }
};

// Same icon-button shape as the home header (see NotifBell).
export function FilterButton({ count = 0, onClick }) {
  return (
    <div onClick={onClick} style={{ position: 'relative', width: 36, height: 36, borderRadius: 10, border: '1px solid var(--border)', background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)', flex: 'none' }}>
      <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.5 10 19 14 21 14 12.5 22 3" /></svg>
      {count > 0 && (
        <span style={{ position: 'absolute', top: -5, right: -5, minWidth: 17, height: 17, borderRadius: 9, background: 'var(--bad)', color: '#fff', fontSize: 11.5, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 4px' }}>{count}</span>
      )}
    </div>
  );
}

/**
 * sections: [{ key, label, options: [{ value, label }] }] — MULTI-select each:
 * tapping toggles a chip in/out, so agents can widen a search (e.g. status =
 * Active + Under offer). value: { [key]: string[] | undefined }.
 * Apply → onApply(draft); Reset → onApply({}) immediately. Both close the sheet.
 */
export default function FilterSheet({ open, title = 'Filters', sections = [], value = {}, onApply, onClose }) {
  const [draft, setDraft] = useState(value);
  useEffect(() => { if (open) setDraft(value); /* eslint-disable-next-line */ }, [open]);
  const { mounted, closing } = useSheetAnim(open);
  if (!mounted) return null;

  const toggle = (k, v) => setDraft((s) => {
    const cur = Array.isArray(s[k]) ? s[k] : s[k] ? [s[k]] : [];
    const next = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    return { ...s, [k]: next.length ? next : undefined };
  });

  return (
    <div onClick={onClose} className={`sheet-scrim${closing ? ' is-closing' : ''}`} style={{ position: 'fixed', inset: 0, zIndex: 4600, background: 'rgba(0,0,0,.45)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
      <div onClick={(e) => e.stopPropagation()} className={`sheet-panel${closing ? ' is-closing' : ''}`} style={{ background: 'var(--surface)', borderRadius: '18px 18px 0 0', maxHeight: '80%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '14px 16px 10px', borderBottom: '1px solid var(--border)', flex: 'none' }}>
          <div style={{ flex: 1, fontSize: 15, fontWeight: 600 }}>{title}</div>
          <div onClick={onClose} style={{ width: 32, height: 32, borderRadius: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--muted)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="5" y1="5" x2="19" y2="19" /><line x1="19" y1="5" x2="5" y2="19" /></svg>
          </div>
        </div>

        <div className="scrx" style={{ flex: 1, overflowY: 'auto', padding: '15px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {sections.map((sec) => (
            <div key={sec.key}>
              <div style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', color: 'var(--muted)', marginBottom: 8, textTransform: 'uppercase' }}>{sec.label}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {sec.options.map((o) => {
                  const active = Array.isArray(draft[sec.key]) ? draft[sec.key].includes(o.value) : draft[sec.key] === o.value;
                  return (
                    <div key={o.value} onClick={() => toggle(sec.key, o.value)} style={{ fontSize: 12.5, fontWeight: 600, padding: '7px 13px', borderRadius: 999, cursor: 'pointer', background: active ? 'var(--primary)' : 'var(--surface2)', color: active ? '#fff' : 'var(--muted)', border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}` }}>
                      {o.label}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, padding: '15px 16px', paddingBottom: 'calc(12px + env(safe-area-inset-bottom))', borderTop: '1px solid var(--border)', flex: 'none' }}>
          <Button block onClick={() => { onApply({}); onClose(); }}>Reset filters</Button>
          <Button block type="primary" onClick={() => { onApply(draft); onClose(); }}>Apply</Button>
        </div>
      </div>
    </div>
  );
}
